/**
 * Element math — bot parity with `utils/combat.ts` and `utils/emojis.ts`.
 * `statistics` display variants are not ported (the UI formats numbers itself).
 */

/** The five elemental attributes plus the level: what every stat bonus reads. */
export interface StatCarrier {
  attributes: { agility: number; chance: number; intelligence: number; neutral: number; strength: number };
  level: { level: number };
}

/** Element that `element` is weak against (`utils/emojis.ts#getWeakness`). */
export function getWeakness(element: string): string {
  switch (element) {
    case 'Intelligence': return 'Chance';
    case 'Chance': return 'Agility';
    case 'Agility': return 'Strength';
    case 'Strength': return 'Intelligence';
    case 'Neutral': return 'Neutral';
    default: return '';
  }
}

/** `calcItemElementDamage`: element-scaled value, floored. */
export function elementDamage(element: string, user: StatCarrier, attack: number): number {
  const a = user.attributes;
  const lvl = user.level.level;
  switch (element) {
    case 'Intelligence': attack *= 1 + (a.intelligence + lvl) / 100; break;
    case 'Chance': attack *= 1 + (a.chance + lvl) / 100; break;
    case 'Agility': attack *= 1 + (a.agility + lvl) / 100; break;
    case 'Strength': attack *= 1 + (a.strength + lvl) / 100; break;
    case 'Neutral': attack *= 1 + (a.neutral + lvl) / 100; break;
    case 'Multi':
      attack *= 1 + ((a.intelligence + a.chance + a.agility + a.strength + a.neutral) / 2 + lvl) / 100;
      break;
  }
  return Math.floor(attack);
}

/** `calcWeaknessDamage`: +25 % when the attacker's element is the defender's weakness. */
export function weaknessDamage(attacker: string, defender: string, attack: number): number {
  return attacker === getWeakness(defender) ? attack * 1.25 : attack;
}

/** `calcStrDamage` (not floored, like the bot). */
export function strDamage(element: string, user: StatCarrier, attack: number): number {
  if (element !== 'Strength') return attack;
  return attack * (1 + 10 / 100 + (user.attributes.strength + user.level.level) / 50 / 100);
}

/** `calcAgiCritChance` in percent points. */
export function agiCritChance(element: string, user: StatCarrier): number {
  return element === 'Agility' ? 5 + (user.attributes.agility + user.level.level) / 100 : 0;
}

/** `calcAgiCritDamage`: ×1.5 crit, boosted for Agility; floored. */
export function agiCritDamage(element: string, user: StatCarrier, attack: number): number {
  let dmg = attack * 1.5;
  if (element === 'Agility') dmg *= 1 + 20 / 100 + (user.attributes.agility + user.level.level) / 25 / 100;
  return Math.floor(dmg);
}

/** `calcChanceFailChance` in percent points (raises the miss threshold). */
export function chanceFailChance(element: string, user: StatCarrier): number {
  return element === 'Chance' ? 5 + (user.attributes.chance + user.level.level) / 100 : 0;
}

/** `calcIntLifeSteal`: HP healed by an Intelligence attacker. */
export function intLifeSteal(element: string, user: StatCarrier, attack: number): number {
  if (element !== 'Intelligence') return 0;
  const lifeStealInt = (user.attributes.intelligence + user.level.level) / 100;
  return Math.floor(1 + attack * (15 / 100 + lifeStealInt / 100));
}
